import { useState } from "react"
import { router } from "@inertiajs/react"
import { Ruler } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  SCALE_PRESETS,
  matchPreset,
  presetToFtPerPx,
  resolveCalibrationTarget,
} from "@/editor-core/calibrate"
import { SAMPLE_SHEET_KEY } from "@/editor-core/document"
import { parseFtIn } from "@/editor-core/geometry"
import { useTakeoffStore } from "@/stores/takeoff-store"
import type { EditorSheetImage } from "@/types/takeoff"

// The top-bar scale realm: the preset picker, calibration status, the
// calibrate mode toggle, and the known-distance entry once two points are
// placed. Reads from the store, so it mounts in any page's top bar — the
// images prop (the active sheet's crops) is what lets a calibration clicked
// inside ONE crop rescale that crop instead of the sheet
// (editor-core/calibrate.ts has the decision rule).
export function ScaleControls({
  blank = false,
  images = [],
}: {
  blank?: boolean
  images?: EditorSheetImage[]
}) {
  const mode = useTakeoffStore((s) => s.mode)
  const setMode = useTakeoffStore((s) => s.setMode)
  const readOnly = useTakeoffStore((s) => s.readOnly)
  const applyCalibration = useTakeoffStore((s) => s.applyCalibration)
  const applyPresetScale = useTakeoffStore((s) => s.applyPresetScale)
  const completeCropCalibration = useTakeoffStore((s) => s.completeCropCalibration)
  const setStatus = useTakeoffStore((s) => s.setStatus)
  const sheetId = useTakeoffStore((s) => s.activeSheetId)
  const roomCount = useTakeoffStore(
    (s) => s.rooms.filter((r) => r.sheetId === (s.activeSheetId ?? SAMPLE_SHEET_KEY)).length
  )
  const calibration = useTakeoffStore((s) => s.calibBySheet[s.activeSheetId ?? SAMPLE_SHEET_KEY])
  const ftPerPx = calibration?.ftPerPx ?? null
  const calibPts = calibration?.calibPts ?? []
  const [input, setInput] = useState("")

  const calibrating = mode === "calibrate"

  const apply = () => {
    const ft = parseFtIn(input)
    if (ft && ft > 0 && sheetId != null) {
      const target = resolveCalibrationTarget({
        pts: calibPts,
        ft,
        ftPerPx,
        roomCount,
        images,
      })
      if (target.kind === "crop") {
        // The crop rescales to make the entered dimension true; the sheet's
        // own calibration stands (hence the `calibration` flag — without it
        // the server would clear what this patch exists to satisfy).
        router.patch(
          `/sheets/${sheetId}/images/${target.imageId}`,
          { scale: target.newScale, calibration: true },
          {
            preserveState: true,
            preserveScroll: true,
            only: ["sheets"],
            onSuccess: () =>
              completeCropCalibration(
                `Floor plan ${target.index + 1} rescaled ×${target.factor.toFixed(2)} ` +
                  `to make that ${input.trim()} — sheet scale unchanged`
              ),
            onError: () =>
              setStatus("Could not rescale the floor plan — rooms may have just been traced"),
          }
        )
        setInput("")
        setMode("select")
        return
      }
    }
    applyCalibration(input)
    // The store rejects unparseable input (and says so in the status line);
    // only a value that parses ends the calibrate session.
    if (parseFtIn(input)) {
      setInput("")
      setMode("select")
    }
  }

  // Blank sheets carry a preset scale — nothing to calibrate.
  if (blank) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-accent-display" data-testid="scale-status">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
        {ftPerPx ? `Preset — 1 ft = ${(1 / ftPerPx).toFixed(0)} px` : "Preset scale"}
      </span>
    )
  }

  const matched = matchPreset(ftPerPx)

  // Which calibration is about to happen — announced BEFORE Apply, so a
  // crop-scoped rescale is never a surprise. Attribution only needs the
  // points, so a placeholder distance resolves it.
  const pendingTarget =
    calibrating && calibPts.length === 2 && ftPerPx != null && sheetId != null
      ? resolveCalibrationTarget({ pts: calibPts, ft: 1, ftPerPx, roomCount, images })
      : null

  return (
    <span className="flex items-center gap-2" data-testid="scale-controls">
      {calibrating && calibPts.length === 2 && !readOnly ? (
        <>
          <Input
            data-testid="calib-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && apply()}
            placeholder={`e.g. 26'0"`}
            className="!h-7 w-28 !py-0 text-sm"
            aria-label="Real-world distance between the two calibration points"
            autoFocus
          />
          <Button size="sm" className="!h-7" data-testid="calib-apply" onClick={apply}>
            Apply
          </Button>
          {pendingTarget?.kind === "crop" && (
            <span className="text-xs text-ink-muted" data-testid="calib-crop-hint">
              Floor plan {pendingTarget.index + 1} only
            </span>
          )}
        </>
      ) : (
        <span
          className={`flex items-center gap-1.5 text-xs ${
            ftPerPx ? "text-accent-display" : "text-signal-display"
          }`}
          data-testid="scale-status"
        >
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${ftPerPx ? "bg-accent" : "bg-signal"}`}
          />
          {calibrating
            ? calibPts.length === 0
              ? "Click two points a known distance apart"
              : "Click the second point"
            : ftPerPx
              ? matched
                ? `${matched.label} · 1 ft = ${(1 / ftPerPx).toFixed(1)} px`
                : `Calibrated · 1 ft = ${(1 / ftPerPx).toFixed(1)} px`
              : "Not calibrated"}
        </span>
      )}
      {!readOnly && !calibrating && (
        <Select
          aria-label="Set the sheet scale from a preset"
          data-testid="scale-preset"
          value=""
          onChange={(e) => {
            const preset = SCALE_PRESETS.find((p) => p.label === e.target.value)
            if (preset) applyPresetScale(presetToFtPerPx(preset), preset.label)
          }}
          className="!h-7 w-32 shrink-0 !py-0 text-xs"
        >
          <option value="" disabled>
            {ftPerPx ? "Change scale…" : "Set scale…"}
          </option>
          {SCALE_PRESETS.map((p) => (
            <option key={p.label} value={p.label}>
              {p.label}
            </option>
          ))}
        </Select>
      )}
      {!readOnly && (
        <Button
          variant={calibrating ? "soft" : "ghost"}
          size="sm"
          className="!h-7"
          data-testid="calibrate-toggle"
          aria-pressed={calibrating}
          title="Calibrate (C) — click two points a known distance apart"
          onClick={() => setMode(calibrating ? "select" : "calibrate")}
        >
          <Ruler className="mr-1 h-3.5 w-3.5" />
          {ftPerPx && !calibrating ? "Recalibrate" : "Calibrate"}
        </Button>
      )}
    </span>
  )
}
